"use client";

import React, { useEffect, useState } from "react";
import { reportClientError } from "@/lib/clientErrorDiagnostics";
import { isDeploymentRecoveryEnabled } from "@/lib/deploymentRecoveryEnvironment";
import {
  coordinatePwaChunkRecovery,
  getPrivacySafeChunkRecoveryError,
  getPwaChunkFailureDetails,
  isChunkLoadError,
} from "@/lib/chunkErrorRecovery";
import {
  getPwaReloadSafety,
  PWA_RELOAD_SAFETY_EVENT,
} from "@/lib/pwaReloadSafety";
import { PWA_DEPLOYMENT_MANUAL_QUERY_KEY } from "@/lib/pwaRuntimeContract";

function shouldLogChunkRecoveryDiagnostics() {
  if (process.env.NEXT_PUBLIC_CHUNK_RECOVERY_DEBUG === "1") return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem("CHUNK_RECOVERY_DEBUG") === "1";
  } catch {
    return false;
  }
}

function logChunkRecoveryDiagnostics(details, result, source) {
  if (!shouldLogChunkRecoveryDiagnostics()) return;
  const pathname =
    typeof window !== "undefined" ? window.location.pathname || "" : "";
  console.warn("[CHUNK_RECOVERY_DIAG]", {
    source,
    result,
    pathname,
    ...details,
  });
}

export async function recoverFromChunkError(error, { source = "recover" } = {}) {
  if (typeof window === "undefined") {
    return { action: "failed", automaticNavigation: false };
  }
  const decision = await coordinatePwaChunkRecovery(error);
  if (decision.action === "ignore" || decision.action === "prefetch_ignored") {
    logChunkRecoveryDiagnostics(decision.diagnostics, decision.action, source);
    return decision;
  }
  reportClientError({
    error: getPrivacySafeChunkRecoveryError(decision.diagnostics),
    source:
      decision.action === "deferred"
        ? "chunk-recovery-deferred"
        : "chunk-recovery",
    pathname: window.location.pathname,
    extra: decision.diagnostics,
  });
  logChunkRecoveryDiagnostics(decision.diagnostics, decision.action, source);
  return decision;
}

export function ChunkRecoveryFallback({
  mode = "refreshing",
  title = "Refreshing YourBarrio...",
  message = "Updating your session...",
}) {
  const isFailed = mode === "failed";
  const isDeferred = mode === "deferred";
  const [safety, setSafety] = useState(() =>
    typeof window === "undefined" ? { offline: false, reason: null } : getPwaReloadSafety()
  );
  const reload = () => {
    if (isDeferred && safety.offline) return;
    if (isDeferred && !safety.safe) {
      const confirmed = window.confirm(
        "The latest unsaved changes may not have reached the server. Reload anyway and restore the last saved version?"
      );
      if (!confirmed) return;
    }
    const target = new URL("/", window.location.origin);
    target.searchParams.set(PWA_DEPLOYMENT_MANUAL_QUERY_KEY, "1");
    window.location.replace(target.href);
  };
  useEffect(() => {
    const update = () => setSafety(getPwaReloadSafety());
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    window.addEventListener(PWA_RELOAD_SAFETY_EVENT, update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      window.removeEventListener(PWA_RELOAD_SAFETY_EVENT, update);
    };
  }, []);
  return (
    <div
      style={{
        alignItems: "center",
        background: "#f8fafc",
        color: "#172033",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "24px",
        width: "100%",
      }}
    >
      <style>{`
        @keyframes yb-chunk-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #dbe3ee",
          borderRadius: "18px",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.12)",
          maxWidth: "448px",
          padding: "28px",
          textAlign: "center",
          width: "100%",
        }}
      >
        <div
          style={{
            alignItems: "center",
            display: "flex",
            height: "56px",
            justifyContent: "center",
            margin: "0 auto 20px",
            width: "56px",
          }}
        >
          <img
            src="/brand/logo-mark-purple.svg"
            alt="YourBarrio"
            style={{
              display: "block",
              height: "56px",
              objectFit: "contain",
              width: "56px",
            }}
          />
        </div>
        <h1
          style={{
            fontSize: "20px",
            fontWeight: 650,
            letterSpacing: 0,
            lineHeight: 1.25,
            margin: 0,
          }}
        >
          {isDeferred
            ? safety.offline
              ? "You’re offline"
              : "Refresh paused"
            : isFailed
              ? "We need one more refresh"
              : title}
        </h1>
        <p
          style={{
            color: "#526070",
            fontSize: "14px",
            lineHeight: "24px",
            margin: "8px 0 0",
          }}
        >
          {isDeferred
            ? safety.reason || "YourBarrio is protecting unsaved work from an automatic reload."
            : isFailed
              ? "This page could not load the latest app files."
              : message}
        </p>
        {isFailed || isDeferred ? (
          <button
            type="button"
            onClick={reload}
            disabled={isDeferred && safety.offline}
            style={{
              background: "#172033",
              border: 0,
              borderRadius: "12px",
              color: "#ffffff",
              cursor: isDeferred && safety.offline ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: 650,
              marginTop: "20px",
              padding: "12px 16px",
              width: "100%",
              opacity: isDeferred && safety.offline ? 0.55 : 1,
            }}
          >
            {isDeferred && safety.offline ? "Reconnect to refresh" : isDeferred && !safety.safe ? "Reload anyway" : isDeferred ? "Reload page" : "Refresh page"}
          </button>
        ) : (
          <div
            aria-hidden="true"
            style={{
              animation: "yb-chunk-spin 0.8s linear infinite",
              border: "4px solid #e4e9f2",
              borderRadius: "999px",
              borderTopColor: "#6f34ff",
              height: "32px",
              margin: "20px auto 0",
              width: "32px",
            }}
          />
        )}
      </div>
    </div>
  );
}

export function ChunkErrorRecoveryListener() {
  const [recoveryState, setRecoveryState] = useState(null);

  useEffect(() => {
    const handleChunkError = async (error, source) => {
      const decision = await recoverFromChunkError(error, { source });
      if (
        decision.action === "refreshing" ||
        decision.action === "failed" ||
        decision.action === "deferred"
      ) {
        setRecoveryState(decision.action);
      }
    };

    const onError = (event) => {
      if (!isDeploymentRecoveryEnabled()) return;
      if (!isChunkLoadError(event)) return;
      event?.preventDefault?.();
      void handleChunkError(event, "window-error");
    };
    const onUnhandledRejection = (event) => {
      if (!isDeploymentRecoveryEnabled()) return;
      if (!isChunkLoadError(event)) return;
      event?.preventDefault?.();
      void handleChunkError(event, "unhandledrejection");
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  if (!recoveryState) return null;
  return (
    <div className="fixed inset-0 z-[2147483647]">
      <ChunkRecoveryFallback mode={recoveryState} />
    </div>
  );
}

export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { recoveryState: null };
  }

  static getDerivedStateFromError(error) {
    if (!isChunkLoadError(error)) return { nonChunkError: error };
    if (!isDeploymentRecoveryEnabled()) return { nonChunkError: error };
    return {
      recoveryState: "refreshing",
    };
  }

  componentDidCatch(error, info) {
    if (!isChunkLoadError(error)) {
      reportClientError({ error, source: "chunk-boundary" });
      if (shouldLogChunkRecoveryDiagnostics()) {
        console.error("[APP_ERROR_BOUNDARY]", error, info);
      }
      return;
    }
    const details =
      typeof window === "undefined"
        ? getPwaChunkFailureDetails(error)
        : getPwaChunkFailureDetails(error, window.location.origin);
    logChunkRecoveryDiagnostics(details, this.state.recoveryState, "boundary");
    void recoverFromChunkError(error, { source: "boundary" }).then((decision) => {
      if (decision.action !== this.state.recoveryState) {
        this.setState({ recoveryState: decision.action });
      }
    });
  }

  render() {
    if (this.state.nonChunkError) {
      throw this.state.nonChunkError;
    }
    if (this.state.recoveryState) {
      return <ChunkRecoveryFallback mode={this.state.recoveryState} />;
    }
    return this.props.children;
  }
}
